import { useTranslation } from "react-i18next";

import { formatPercent, formatPrice, splitCountdown } from "@/shared/lib";
import { ProgressBar } from "@/shared/ui";

import { asOfferState, type LimitedOffer } from "../model/types";

/**
 * Шкала заполненности пула и обратный отсчёт (доработки §2.2).
 *
 * Все три числа — `pool_used`, `pool_limit`, `fill_pct` — приходят с бэкенда
 * посчитанными. Доля здесь не вычисляется: это арифметика над деньгами, и она
 * живёт только в `shared/lib/money.ts`.
 *
 * Отсчёт стартует от `seconds_left`, измеренного СЕРВЕРНЫМИ часами, а не от
 * разницы с `Date.now()`. §10 доработок требует, чтобы срок не зависел от
 * локального времени клиента, а QA-чеклист §13 велит проверить ручную смену
 * времени устройства — с вычитанием из часов устройства такая проверка провалилась
 * бы. Отсчёт статический, без секундного тика: карточка в списке, дёргающаяся
 * раз в секунду, читается хуже, а обновляет её перезапрос каталога.
 */
function Countdown({ offer }: { offer: LimitedOffer }) {
  const { t } = useTranslation();
  const { days, hours, minutes } = splitCountdown(offer.seconds_left);

  if (offer.seconds_left <= 0) return null;
  if (days > 0) return <>{t("robots.limited.countdown", { days, hours })}</>;
  if (hours > 0) return <>{t("robots.limited.countdownHours", { hours, minutes })}</>;
  return <>{t("robots.limited.countdownMinutes", { minutes })}</>;
}

export function OfferProgress({
  offer,
  currency = "USDT",
}: {
  offer: LimitedOffer;
  currency?: string;
}) {
  const { t } = useTranslation();
  const state = asOfferState(offer.state);
  const used = formatPrice(offer.pool_used);
  const limit = formatPrice(offer.pool_limit, { suffix: currency });
  // Процент — через общий форматтер: «69,00%» с запятой, как везде. Сырая
  // строка контракта («69.00») выглядела бы иностранной цифрой посреди
  // русского интерфейса.
  const pct = formatPercent(offer.fill_pct);

  return (
    <div className="flex flex-col gap-1.5">
      <ProgressBar
        valuePct={offer.fill_pct}
        label={t("robots.limited.poolLabel")}
        // Почти заполненный пул — это срочность, а не ошибка: жёлтый, не красный.
        // Красным гаснет только исчерпанный, и он же выключает кнопку.
        tone={state === "full" ? "loss" : state === "almost_full" ? "warning" : "accent"}
        valueText={t("robots.limited.progress", { used, limit, pct })}
      />
      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-micro font-medium text-text-tertiary">
        <span className="font-semibold text-text-secondary">
          {t("robots.limited.progress", { used, limit, pct })}
        </span>
        {offer.accepts_entries ? (
          <>
            <span aria-hidden="true">·</span>
            <span>
              <Countdown offer={offer} />
            </span>
          </>
        ) : null}
      </div>
    </div>
  );
}
